<template>
  <div class="fermat-principle-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$T = ∫ (n/c) ds = min$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 500 220" ref="svgCanvas">
           <!-- Two media separator -->
           <rect x="0" y="0" width="500" height="110" fill="#0f172a" />
           <rect x="0" y="110" width="500" height="110" fill="rgba(6, 182, 212, 0.15)" />
           <line x1="0" y1="110" x2="500" y2="110" stroke="#334155" stroke-width="2" />
           
           <text x="10" y="20" fill="#94a3b8" font-size="10">介质 1 (空气, n₁ = 1.0) - 传播快</text>
           <text x="10" y="210" fill="#06b6d4" font-size="10">介质 2 (水面, n₂ = 1.5) - 传播慢</text>

           <!-- Start (A) and End (B) Points -->
           <circle :cx="A.x" :cy="A.y" r="5" fill="#fcd34d" />
           <text :x="A.x - 15" :y="A.y - 8" fill="#fcd34d" font-size="11" font-weight="bold">A</text>
           
           <circle :cx="B.x" :cy="B.y" r="5" fill="#22c55e" />
           <text :x="B.x + 8" :y="B.y + 12" fill="#22c55e" font-size="11" font-weight="bold">B</text>

           <!-- Normal line at interface -->
           <line :x1="currentX" y1="60" :x2="currentX" y2="160" stroke="#475569" stroke-dasharray="4,4" />

           <!-- Highlight optimal theoretical path -->
           <polyline :points="`${A.x},${A.y} ${optimalX},110 ${B.x},${B.y}`" fill="none" stroke="rgba(252, 211, 77, 0.2)" stroke-width="2" stroke-dasharray="4,4" />
           <text :x="optimalX - 10" y="130" fill="rgba(252, 211, 77, 0.5)" font-size="9">理论最速</text>

           <!-- User interactive path -->
           <g>
               <polyline :points="`${A.x},${A.y} ${currentX},110 ${B.x},${B.y}`" fill="none" :stroke="isOptimal ? '#06b6d4' : '#ef4444'" stroke-width="3" />
               <circle :cx="currentX" cy="110" r="8" :fill="isOptimal ? '#06b6d4' : '#ef4444'" stroke="#fff" stroke-width="2" style="cursor: ew-resize;" @mousedown="startDrag" @touchstart.prevent="startDragTouch" />
               <text :x="currentX - 12" y="95" :fill="isOptimal ? '#06b6d4' : '#ef4444'" font-size="12" font-weight="bold">P</text>
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>P点光程总耗时 T</label>
              <span class="val" :style="{color: isOptimal ? '#22c55e' : '#f8fafc'}">{{ (currentTime * 10).toFixed(2) }} <small>驻点单位</small></span>
           </div>
           <div class="hud-item orange">
              <label>理论极限极小值</label>
              <span class="val">{{ (optimalTime * 10).toFixed(2) }} <small>驻点单位</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">左右拖动界面落点 P (x)</label>
                    <el-slider v-model="currentX" :min="50" :max="450" :step="0.5" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>费马原理强调：光从空间一点 A 传播到另一点 B，其真实路径总是使光程积分（即传播时间）达到**极小值**。演示中介质1(空气)光速快，介质2(水)光速慢。如果你尝试拖走捷径(两点直线)，在水里走的慢路会变长，总耗时反而更大。只有在这个**精确折射点**，光选择了"在空气中多走点，在水中少走点"的最优折中策略。当 <code>T</code> 最小化时，恰好满足斯涅尔折射定律 <code>n1·sin(θ1) = n2·sin(θ2)</code>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FermatPrincipleViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      A: { x: 50, y: 40 },
      B: { x: 450, y: 180 },
      n1: 1.0,
      n2: 1.5,
      currentX: 180,
      optimalX: 0
    }
  },
  computed: {
    currentTime() {
       const d1 = Math.sqrt(Math.pow(this.currentX - this.A.x, 2) + Math.pow(110 - this.A.y, 2));
       const d2 = Math.sqrt(Math.pow(this.B.x - this.currentX, 2) + Math.pow(this.B.y - 110, 2));
       return (d1 * this.n1 + d2 * this.n2) / 100;
    },
    optimalTime() {
       const d1 = Math.sqrt(Math.pow(this.optimalX - this.A.x, 2) + Math.pow(110 - this.A.y, 2));
       const d2 = Math.sqrt(Math.pow(this.B.x - this.optimalX, 2) + Math.pow(this.B.y - 110, 2));
       return (d1 * this.n1 + d2 * this.n2) / 100;
    },
    isOptimal() {
       // Tolerance strictly checks if the time is near optimal minimum
       return (this.currentTime - this.optimalTime) < 0.005;
    }
  },
  methods: {
    findOptimalX() {
       let minTime = Infinity;
       let optX = 50;
       for (let x = 50; x <= 450; x += 0.1) {
          const d1 = Math.sqrt(Math.pow(x - this.A.x, 2) + Math.pow(110 - this.A.y, 2));
          const d2 = Math.sqrt(Math.pow(this.B.x - x, 2) + Math.pow(this.B.y - 110, 2));
          const t = d1 * this.n1 + d2 * this.n2;
          if (t < minTime) {
             minTime = t;
             optX = x;
          }
       }
       this.optimalX = optX;
    },
    stopDrag() {
       document.removeEventListener('mousemove', this.onMove);
       document.removeEventListener('mouseup', this.stopDrag);
       document.removeEventListener('touchmove', this.onTouchMove);
       document.removeEventListener('touchend', this.stopDrag);
    },
    onMove(e) {
       this.handleMove(e.clientX);
    },
    onTouchMove(e) {
       this.handleMove(e.touches[0].clientX);
    },
    handleMove(clientX) {
       let dx = clientX - this.startClientX;
       let newX = this.startX + dx;
       this.currentX = Math.max(50, Math.min(450, newX));
    },
    startDrag(e) {
       this.startClientX = e.clientX;
       this.startX = this.currentX;
       document.addEventListener('mousemove', this.onMove);
       document.addEventListener('mouseup', this.stopDrag);
    },
    startDragTouch(e) {
       this.startClientX = e.touches[0].clientX;
       this.startX = this.currentX;
       document.addEventListener('touchmove', this.onTouchMove, {passive: false});
       document.addEventListener('touchend', this.stopDrag);
    }
  },
  mounted() {
    this.findOptimalX();
    this.currentX = this.optimalX; // Auto snap at start
  },
  beforeDestroy() {
     this.stopDrag();
  }
}
</script>

<style scoped lang="scss">
.fermat-principle-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; user-select: none; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } code { background: #e2e8f0; padding: 2px 4px; border-radius: 4px; font-family: monospace; } } }
</style>
